import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Tenant, Permission, AuthContextType, DEFAULT_PERMISSIONS } from '../types/auth';
import { tenantMockService } from '../services/tenantMockService';

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on app start
    const storedUser = localStorage.getItem('user');
    const storedTenant = localStorage.getItem('tenant');

    if (storedUser && storedTenant) {
      setUser(JSON.parse(storedUser));
      setTenant(JSON.parse(storedTenant));
    }

    setIsLoading(false);
  }, []);

  const permissions: Permission[] = user ? DEFAULT_PERMISSIONS[user.role] || [] : [];

  const hasPermission = (resource: string, action: Permission['action']): boolean => {
    return permissions.some(perm => perm.resource === resource && perm.action === action);
  };

  const hasAnyPermission = (permissionChecks: Array<{resource: string, action: Permission['action']}>): boolean => {
    return permissionChecks.some(check => hasPermission(check.resource, check.action));
  };

  const login = async (username: string, password: string): Promise<void> => {
    setIsLoading(true);

    try {
      // For demo: use deterministic mock login and ignore password
      const { user: demoUser, tenant: demoTenant } = await tenantMockService.loginAs(username || 'user-1');

      const userData: User = {
        ...demoUser,
        lastLogin: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: 'active'
      };

      const tenantData: Tenant = {
        ...demoTenant,
        status: demoTenant.status,
        updatedAt: new Date().toISOString(),
      };

      setUser(userData);
      setTenant(tenantData);

      localStorage.setItem('authToken', 'demo-token');
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('tenant', JSON.stringify(tenantData));

    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setTenant(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');
  };

  const value: AuthContextType = {
    user,
    tenant,
    permissions,
    hasPermission,
    hasAnyPermission,
    login,
    logout,
    isLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
